import wkx from 'wellknown';

// JSZip n'est plus utilisé pour l'export (remplacé par le streaming)
// Il peut rester dans package.json si MapView en a besoin ailleurs.

// ── Modes d'export disponibles ────────────────────────────────────────────────

export const GEOJSON_EXPORT_MODES = [
    {
        key: 'latlon',
        label: 'Points Lat / Lon',
        description: 'Un point par bâtiment (meta.latitude / meta.longitude)',
        mappingKey: 'adresseBanMap',
    },
    {
        key: 'geom_cstr',
        label: 'batiment_construction.geom_cstr',
        description: 'Géométrie de construction (batiment_construction)',
        sourceField: 'batiment_construction.geom_cstr',
        multiRow: true,
        mappingKey: 'batimentConstructionByIdMap'
    },
    {
        key: 'geom_groupe',
        label: 'batiment_groupe.geom_groupe',
        description: 'Emprise du groupe bâtiment',
        sourceField: 'batiment_groupe.geom_groupe',
        multiRow: false,
        mappingKey: 'adresseBatimentGroupeMap'
    },
    {
        key: 'geom_parcelle',
        label: 'parcelles.geom_parcelle',
        description: 'Géométrie de la parcelle cadastrale',
        sourceField: 'parcelles.geom_parcelle',
        multiRow: true,  // un groupe peut couvrir plusieurs parcelles
        mappingKey: 'parcellesByParcelleIdMap',
    },
];

// ── Helpers ───────────────────────────────────────────────────────────────────


/** Parse une géométrie → objet GeoJSON geometry (ou null si invalide).
 *  Supporte : objet GeoJSON, GeoJSON stringifié {"type":"MultiPolygon",...} et WKT POINT(...) */
function wktToGeometry(raw) {
    if (!raw) return null;

    // ── Objet GeoJSON déjà parsé ──────────────────────────────────────────────
    if (typeof raw === 'object') {
        return (raw.type && (raw.coordinates || raw.geometries)) ? raw : null;
    }

    if (typeof raw !== 'string') return null;
    const clean = raw.replace(/^SRID=\d+;/i, '').trim();
    if (!clean) return null;

    // ── GeoJSON stringifié ────────────────────────────────────────────────────
    if (clean.startsWith('{')) {
        try {
            const geom = JSON.parse(clean);
            if (geom && geom.type && (geom.coordinates || geom.geometries)) return geom;
        } catch { /* continuer */ }
    }

    // ── WKT fallback ──────────────────────────────────────────────────────────
    try {
        const geom = wkx.parse(clean);
        return geom ?? null;
    } catch {
        return null;
    }
}

/** Construit les properties GeoJSON à partir des clés visibles d'une row. */
function buildProperties(row, visibleKeys) {
    const properties = {};
    visibleKeys.forEach(key => {
        const v = row[key];
        const val = Array.isArray(v) ? v.join(' | ') : (v ?? null);
        if (val !== null && val !== '' && val !== undefined) {
            properties[key] = val;
        }
    });
    return properties;
}

/** Déclenche le téléchargement d'un Blob (fallback quand showSaveFilePicker indisponible). */
function downloadBlob(blob, filename) {
    const url  = URL.createObjectURL(blob);
    const link = Object.assign(document.createElement('a'), { href: url, download: filename });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// ── Constructeurs de features par mode ───────────────────────────────────────

function featuresLatLon(rows, visibleKeys, filteredIds) {
    return Object.entries(rows).flatMap(([id, row]) => {
        if (filteredIds && !filteredIds.has(id)) return [];
        const lat = Number(row['meta.latitude']);
        const lon = Number(row['meta.longitude']);
        if (!lat && !lon) return [];
        return [{
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lon, lat] },
            properties: buildProperties(row, visibleKeys),
        }];
    });
}

/**
 * Pour les champs multi-valeurs (adresses), on explose :
 * une feature par valeur de géométrie (chaque adresse → une feature).
 * Si deduplicateGeom=true, les WKT identiques sont dédupliqués (utile pour geom_cstr
 * où la même géométrie de construction est répétée pour chaque adresse).
 */
function featuresFromWktArray(rows, visibleKeys, sourceField, filteredIds, deduplicateGeom = false) {
    return Object.entries(rows).flatMap(([id, row]) => {
        if (filteredIds && !filteredIds.has(id)) return [];
        const rawValues = row[sourceField];
        let wktList = Array.isArray(rawValues)
            ? rawValues
            : (rawValues != null ? [rawValues] : []);

        // Pour geom_cstr : la géométrie de construction est identique pour toutes
        // les adresses d'une même construction → dédupliquer pour n'avoir qu'une feature
        if (deduplicateGeom) {
            wktList = [...new Set(wktList.filter(v => v != null))];
        }

        return wktList.flatMap(wkt => {
            const geometry = wktToGeometry(wkt);
            if (!geometry) return [];
            return [{
                type: 'Feature',
                geometry,
                properties: buildProperties(row, visibleKeys),
            }];
        });
    });
}

/**
 * Pour les champs mono-valeur (batiment_groupe), on prend la première
 * valeur non-nulle.
 */
function featuresFromWktSingle(rows, visibleKeys, sourceField, filteredIds) {
    return Object.entries(rows).flatMap(([id, row]) => {
        if (filteredIds && !filteredIds.has(id)) return [];
        const rawValues = row[sourceField];
        const wktList = Array.isArray(rawValues)
            ? rawValues
            : (rawValues != null ? [rawValues] : []);

        const geometry = wktList.reduce((found, wkt) => found ?? wktToGeometry(wkt), null);
        if (!geometry) return [];
        return [{
            type: 'Feature',
            geometry,
            properties: buildProperties(row, visibleKeys),
        }];
    });
}

// ── Générateur : produit une feature à la fois (évite le tableau complet en RAM) ──

const CHUNK_FEATURES = 500; // features par écriture

/**
 * Générateur qui yield une feature GeoJSON à la fois selon le mode,
 * sans jamais construire le tableau complet en mémoire.
 */
function* iterateFeatures(csvData, propertyKeys, mode, filteredIds) {
    for (const [id, row] of Object.entries(csvData.rows)) {
        if (filteredIds && !filteredIds.has(id)) continue;

        if (mode.key === 'latlon') {
            const lat = Number(row['meta.latitude']);
            const lon = Number(row['meta.longitude']);
            if (!lat && !lon) continue;
            yield {
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lon, lat] },
                properties: buildProperties(row, propertyKeys),
            };
        } else if (mode.multiRow || mode.key === 'geom_cstr' || mode.key === 'geom_parcelle') {
            const rawValues = row[mode.sourceField];
            let wktList = Array.isArray(rawValues) ? rawValues : (rawValues != null ? [rawValues] : []);
            if (mode.key === 'geom_cstr' || mode.key === 'geom_parcelle') {
                wktList = [...new Set(wktList.filter(v => v != null))];
            }
            for (const wkt of wktList) {
                const geometry = wktToGeometry(wkt);
                if (!geometry) continue;
                yield {
                    type: 'Feature',
                    geometry,
                    properties: buildProperties(row, propertyKeys),
                };
            }
        } else {
            const rawValues = row[mode.sourceField];
            const wktList = Array.isArray(rawValues) ? rawValues : (rawValues != null ? [rawValues] : []);
            const geometry = wktList.reduce((found, wkt) => found ?? wktToGeometry(wkt), null);
            if (!geometry) continue;
            yield {
                type: 'Feature',
                geometry,
                properties: buildProperties(row, propertyKeys),
            };
        }
    }
}

// ── Export principal ──────────────────────────────────────────────────────────

/**
 * Construit le tableau de features GeoJSON selon le mode choisi.
 * Réutilisable depuis MapView ou tout autre composant.
 */
/**
 * Champs toujours inclus dans les propriétés GeoJSON (pour les popups) même si cachés dans le tableau.
 * meta.index = clé entière de csvData.rows → utilisé par StackPopupContent pour la lookup.
 */
const ALWAYS_IN_PROPERTIES = ['meta.index', 'meta.batiment_construction_id', 'meta.batiment_groupe_id'];

export function buildGeoJsonFeatures(csvData, columnVisibilityModel, modeKey = 'latlon', filteredIds = null) {
    const visibleKeys = Object.keys(csvData.meta).filter(key =>
        columnVisibilityModel[key.replace(/\./g, '__')] !== false
    );
    // Forcer les IDs dans les propriétés même s'ils ne sont pas dans csvData.meta
    const propertyKeys = [...new Set([
        ...ALWAYS_IN_PROPERTIES,
        ...visibleKeys,
    ])];
    const mode = GEOJSON_EXPORT_MODES.find(m => m.key === modeKey) ?? GEOJSON_EXPORT_MODES[0];

    if (mode.key === 'latlon') return featuresLatLon(csvData.rows, propertyKeys, filteredIds);
    if (mode.key === 'geom_cstr' || mode.key === 'geom_parcelle')
        return featuresFromWktArray(csvData.rows, propertyKeys, mode.sourceField, filteredIds, true);
    if (mode.multiRow)  return featuresFromWktArray(csvData.rows, propertyKeys, mode.sourceField, filteredIds);
    return                      featuresFromWktSingle(csvData.rows, propertyKeys, mode.sourceField, filteredIds);
}

/**
 * Génère et télécharge un GeoJSON selon le mode choisi.
 *
 * Stratégie mémoire :
 *  1. showSaveFilePicker  → écriture en streaming par tranches (0 copie globale en RAM)
 *     Le fichier est sauvegardé directement en .geojson (plus de JSZip).
 *  2. Blob(parts[])       → fallback : JSON minifié feature par feature, sans JSZip
 *
 * @param {Object}   csvData               - { rows, meta }
 * @param {Object}   columnVisibilityModel - { [field__]: boolean }
 * @param {string}   modeKey               - clé dans GEOJSON_EXPORT_MODES
 * @param {Set|null} filteredIds           - IDs des lignes visibles (null = toutes)
 */
export async function exportToGeoJsonZip(csvData, columnVisibilityModel, modeKey = 'latlon', filteredIds = null) {
    const visibleKeys = Object.keys(csvData.meta).filter(key =>
        columnVisibilityModel[key.replace(/\./g, '__')] !== false
    );
    const propertyKeys = [...new Set([...ALWAYS_IN_PROPERTIES, ...visibleKeys])];
    const mode = GEOJSON_EXPORT_MODES.find(m => m.key === modeKey) ?? GEOJSON_EXPORT_MODES[0];
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename  = `export-${timestamp}.geojson`;
    const enc       = new TextEncoder();


    // ── 1. Streaming via File System Access API (Chrome / Edge) ──────────────
    if ('showSaveFilePicker' in window) {
        try {
            const handle = await window.showSaveFilePicker({
                suggestedName: filename,
                types: [{ description: 'GeoJSON', accept: { 'application/geo+json': ['.geojson'] } }],
            });
            const writable = await handle.createWritable();
            await writable.write('{"type":"FeatureCollection","features":[\n');

            let first = true;
            let chunk = [];
            for (const feature of iterateFeatures(csvData, propertyKeys, mode, filteredIds)) {
                chunk.push(feature);
                if (chunk.length >= CHUNK_FEATURES) {
                    let str = '';
                    for (const f of chunk) {
                        str += (first ? '' : ',\n') + JSON.stringify(f);
                        first = false;
                    }
                    await writable.write(enc.encode(str));
                    chunk = [];
                }
            }
            if (chunk.length > 0) {
                let str = '';
                for (const f of chunk) {
                    str += (first ? '' : ',\n') + JSON.stringify(f);
                    first = false;
                }
                await writable.write(enc.encode(str));
            }
            await writable.write('\n]}');
            await writable.close();
            return;
        } catch (e) {
            if (e.name === 'AbortError') return; // annulé par l'utilisateur
            // Autre erreur → fallback ci-dessous
        }
    }

    // ── 2. Fallback : Blob multi-parts, JSON minifié, sans JSZip ─────────────
    const parts = ['{"type":"FeatureCollection","features":[\n'];
    let first = true;
    let chunk = [];
    for (const feature of iterateFeatures(csvData, propertyKeys, mode, filteredIds)) {
        chunk.push(feature);
        if (chunk.length >= CHUNK_FEATURES) {
            let str = '';
            for (const f of chunk) {
                str += (first ? '' : ',\n') + JSON.stringify(f);
                first = false;
            }
            parts.push(str);
            chunk = [];
        }
    }
    if (chunk.length > 0) {
        let str = '';
        for (const f of chunk) {
            str += (first ? '' : ',\n') + JSON.stringify(f);
            first = false;
        }
        parts.push(str);
    }
    parts.push('\n]}');

    downloadBlob(
        new Blob(parts, { type: 'application/geo+json' }),
        filename,
    );
}
